import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import {
  Form,
  Row,
  Col,
  Popover,
} from 'antd';
import PropTypes from 'prop-types';
import { WarningOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import axios from 'axios';

import Permissions from '../auth/Permissions';

import FormTextInput from '../common/inputs/FormTextInput';
import FormNumberInput from '../common/inputs/FormNumberInput';
import FormCurrencyInput from '../common/inputs/FormCurrencyInput';
import DivisionSelector from '../common/inputs/DivisionSelector';
import VendorSelector from '../common/inputs/VendorSelector';
import MaterialUnitsSelector from '../common/inputs/MaterialUnitsSelector';
import DisplayText from '../common/text/DisplayText';
import IntegrationTag from '../common/IntegrationTag';

import { markupPercentFormatter, percentParser, materialCostFormatter, currencyFormatter } from '../helpers/inputParsers';

import {
  getMarkupString,
  getDerivedPriceValue,
  MATERIAL_COST_MAX_DECIMALS,
  MATERIAL_MARKUP_MAX_DECIMALS,
} from './materialsHelpers';
import MaterialLocation from './MaterialLocation';
import AssemblyMaterials from './AssemblyMaterials';
import Colors from '../constants/Colors';
import {
  FOOTER_HEIGHT,
  HEADER_HEIGHT,
  TABS_NAVIGATION_HEIGHT,
} from '../constants/Styles';

import CostcodeDisplay from '../costcodes/CotscodeDisplay';
import CostcodeSelector from '../costcodes/CostcodeSelector';
import CostcodesTreeSelector from '../costcodes/CostcodesTreeSelector';

import {
  getEligibleCategoriesForCostcode,
  getQuantityTrackingCategoryIds,
} from '../helpers/costcodeHelpers';
import { getIdMap } from '../helpers/helpers';

const formLabelStyle = {
  style: {
    paddingBottom: 5,
    marginTop: 10,
  },
};

const brokenStyle = {
  color: 'red',
  fontWeight: 900,
};

const onPriceBlur = (form, priceKey, costKey) => {
  if (!form || form.getFieldValue(costKey)) return;
  form.setFieldsValue({ [priceKey]: 0 });
};

export default function MaterialInfo({
  item,
  isNotDisplay,
  divisionId,
  onDivisionChange,
  locations = [],
  onLocationsChanged,
  totalQuantity,
  totalQuantityAllocated,
  isAssembly,
  assemblyMaterials = [],
  onMaterialsChanged,
  shouldAutoCalcCost,
  shouldAutoCalcLabourCost,
  projectId,
}) {
  const vendors = useSelector((state) => state.vendors.vendors);
  const quickbookIntegrations = useSelector((state) => state.settings.quickbookIntegrations);
  const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);

  const quantityTrackingCategoryIds = useMemo(
    () => getQuantityTrackingCategoryIds(categoriesIdMap),
    [categoriesIdMap],
  );

  const filterCostcode = useCallback((cc) => (
    getEligibleCategoriesForCostcode(cc, categoriesIdMap, { filter: 'quantityTracking' }).length > 0
  ), [categoriesIdMap]);

  const isAddingNewMaterial = !item || !item.id;
  const showAdditionalFields = !projectId && !isAssembly;

  const maxHeight = useMemo(() => {
    let offset = TABS_NAVIGATION_HEIGHT;
    if (!isAddingNewMaterial) offset += HEADER_HEIGHT;
    if (Permissions.hasWrite('MATERIALS')) offset += FOOTER_HEIGHT;
    return `calc(100vh - ${offset}px)`;
  }, [isAddingNewMaterial, Permissions]);

  const {
    id,
    code,
    name,
    description,
    partNumber,
    supplier,
    cost,
    markup,
    labourMarkup,
    units,
    vendorId,
    eclipseId,
    quantityThreshold,
    intuitId,
    labourCost,
    costcodeId,
    purchaseTaxCodeId,
  } = item || {};

  const [allTaxCodes, setAllTaxCodes] = useState({});

  const vendor = useMemo(() => (vendorId ? vendors[vendorId] : null), [vendors, vendorId]);
  const vendorName = useMemo(() => vendor?.name || '', [vendor]);

  const qbIntegration = useMemo(() => (
    quickbookIntegrations?.find((integration) => (
      integration.divisions?.includes(divisionId)
    ))
  ), [quickbookIntegrations, divisionId]);

  const taxCodes = useMemo(() => (
    (allTaxCodes[divisionId] ?? []).map((tc) => ({ label: tc.Name, value: tc.Id }))
  ), [allTaxCodes, divisionId]);

  const taxCodesMap = useMemo(() => (
    getIdMap(taxCodes, 'value')
  ), [taxCodes]);

  useEffect(() => {
    if (!qbIntegration?.id) return;
    axios.get('/quickbooks/taxcodes')
      .then(({ data = {} }) => setAllTaxCodes(data))
      .catch(() => {});
  }, [qbIntegration?.id]);

  const safeTotal = totalQuantity ?? 0;

  const getFormWarningLabel = (label, shouldShowWarning) => {
    if (!shouldShowWarning || !isAssembly) return label;
    return (
      <>
        {label}
        <Popover
          placement="bottomLeft"
          trigger="hover"
          content={`${label} has been manual updated and will not be auto-calculated`}
          title="Warning"
        >
          <WarningOutlined
            style={{
              color: Colors.ONTRACCR_DARK_YELLOW,
              marginLeft: 10,
              marginBottom: 1,
            }}
          />
        </Popover>
      </>
    );
  };

  const costcodeSelector = useMemo(() => (
    projectId
      ? <CostcodesTreeSelector
        projectId={projectId}
        divisionIds={[divisionId]}
        includeGlobal={false}
        includeCategoryIds
        filterCostcode={filterCostcode}
      />
      : <CostcodeSelector
        onlyActive
        divisionIds={[divisionId]}
        onlyGlobal
        categoryIds={quantityTrackingCategoryIds}
      />
    ), [projectId, divisionId, quantityTrackingCategoryIds, filterCostcode]);

  return (
    <div className="material-tab-pane-container" style={{ maxHeight }}>
      <FormTextInput
        name="name"
        label="Name"
        isNotDisplay={isNotDisplay}
        value={name}
        rules={[
          { required: isNotDisplay, message: 'Please enter a name' },
        ]}
      />
      <FormTextInput
        textarea
        autoSize
        name="description"
        label="Description"
        value={description}
        isNotDisplay={isNotDisplay}
        rules={[
          { required: isNotDisplay, message: 'Please enter a description' },
        ]}
      />
      {!isAssembly && (
        <Row gutter={20} justify="space-between" align="middle">
          <Col span={isNotDisplay ? 24 : 12}>
            <FormTextInput
              name="partNumber"
              label="Part Number"
              value={partNumber}
              isNotDisplay={isNotDisplay}
              rules={[
                { required: isNotDisplay, message: 'Please enter a part number' },
              ]}
            />
          </Col>
          {!isNotDisplay && (
            <Col span={12}>
              <FormTextInput
                name="code"
                label="Code"
                isNotDisplay={false}
                value={code}
              />
            </Col>
          )}
        </Row>
      )}
      <Form.Item
        name="divisionId"
        label="Division"
        style={{ marginBottom: 0, paddingBottom: -8 }}
        labelCol={formLabelStyle}
        rules={[{ required: isNotDisplay, message: 'Please enter a division' }]}
        valuePropName="divisionId"
      >
        <DivisionSelector
          onChange={onDivisionChange}
          displayMode={!isNotDisplay || projectId}
        />
      </Form.Item>
      <FormTextInput
        name="supplier"
        label="Supplier"
        isNotDisplay={isNotDisplay}
        value={supplier}
      />
      <Form.Item
        name="vendorId"
        label="Vendor"
        style={{ marginBottom: 0, paddingBottom: -8 }}
        labelCol={formLabelStyle}
        getValueFromEvent={(value) => value ?? null}
      >
        {isNotDisplay ? (
          <VendorSelector
            divisionIds={divisionId ? [divisionId] : []}
          />
        ) : <DisplayText title={vendorName} />}
      </Form.Item>
      <Row gutter={20} justify="space-between" align="middle">
        <Col span={isAssembly ? 24 : 8}>
          <FormCurrencyInput
            name="cost"
            label={getFormWarningLabel('Cost', !shouldAutoCalcCost)}
            isNotDisplay={isNotDisplay}
            value={cost ? materialCostFormatter(Number.parseFloat(cost)) : cost}
            precision={MATERIAL_COST_MAX_DECIMALS}
            step={0.00001}
            formatter={materialCostFormatter}
          />
        </Col>
        {!isAssembly && (
          <>
            <Col span={8}>
              <FormNumberInput
                name="markup"
                label="Markup"
                isNotDisplay={isNotDisplay}
                numberInputProps={{
                  formatter: markupPercentFormatter,
                  parser: percentParser,
                  defaultValue: 0,
                  precision: MATERIAL_MARKUP_MAX_DECIMALS,
                  step: 0.00001,
                }}
                value={getMarkupString(markup)}
              />
            </Col>
            <Col span={8}>
              <Form.Item noStyle shouldUpdate={() => false}>
                {(form) => (
                  <FormCurrencyInput
                    name="price"
                    label="Price"
                    isNotDisplay={isNotDisplay}
                    value={getDerivedPriceValue(cost, markup, true)}
                    id={isNotDisplay ? 'material-form-price' : null}
                    onBlur={() => onPriceBlur(form, 'price', 'cost')}
                  />
                )}
              </Form.Item>
            </Col>
          </>
        )}
      </Row>
      <Row gutter={20} justify="space-between" align="middle">
        <Col span={isAssembly ? 24 : 8}>
          <FormCurrencyInput
            name="labourCost"
            label={getFormWarningLabel('Labour Cost', !shouldAutoCalcLabourCost)}
            isNotDisplay={isNotDisplay}
            value={labourCost ? currencyFormatter(labourCost) :labourCost}
          />
        </Col>
        {!isAssembly && (
          <>
            <Col span={8}>
              <FormNumberInput
                name="labourMarkup"
                label="Labour Markup"
                isNotDisplay={isNotDisplay}
                numberInputProps={{
                  formatter: markupPercentFormatter,
                  parser: percentParser,
                  defaultValue: 0,
                  precision: MATERIAL_MARKUP_MAX_DECIMALS,
                  step: 0.00001,
                }}
                value={getMarkupString(labourMarkup)}
              />
            </Col>
            <Col span={8}>
              <Form.Item noStyle shouldUpdate={() => false}>
                {(form) => (
                  <FormCurrencyInput
                    name="labourPrice"
                    label="Labour Price"
                    isNotDisplay={isNotDisplay}
                    value={getDerivedPriceValue(labourCost, labourMarkup, true)}
                    id={isNotDisplay ? 'material-form-labour-price' : null}
                    onBlur={() => onPriceBlur(form, 'labourPrice', 'labourCost')}
                  />
                )}
              </Form.Item>
            </Col>
          </>
        )}
      </Row>
      {!isAssembly && (
        <>
          <Form.Item
            name="units"
            label="Units"
            style={{ marginBottom: 0, paddingBottom: -8 }}
            labelCol={formLabelStyle}
            trigger="onSelect"
          >
            {isNotDisplay
              ? (
                <MaterialUnitsSelector
                  dropdownMatchSelectWidth
                  dropdownWidth="100%"
                />
              )
              : <DisplayText title={units} />}
          </Form.Item>
          <Form.Item
            name={projectId ? 'phaseCostcodeKey' : 'costcodeId'}
            label="Cost Code"
            style={{ marginBottom: 0, paddingBottom: -8 }}
            labelCol={formLabelStyle}
          >
            {
                isNotDisplay ? costcodeSelector : <CostcodeDisplay costcodeId={costcodeId} />
              }
          </Form.Item>
        </>
      )}
      {showAdditionalFields && !isNotDisplay
       && (
       <Form.Item
         name="integrations"
         key="integrations"
         label="Integrations"
         style={{ marginBottom: 0 }}
       >
         <IntegrationTag id={eclipseId} title="Eclipse" />
         <IntegrationTag id={intuitId} title="QuickBooks" />
       </Form.Item>
       )}
      {showAdditionalFields && intuitId && !isNotDisplay && (
        <Form.Item
          label="QuickBooks Tax Code"
          style={{ marginBottom: 0, paddingBottom: 0 }}
          labelCol={formLabelStyle}
        >
          <DisplayText
            title={taxCodesMap[purchaseTaxCodeId]?.label ?? 'None'}
          />
        </Form.Item>
      )}
      {showAdditionalFields && (
        <>
          <Row gutter={20} justify="space-between" align="middle">
            <Col span={12}>
              <FormNumberInput
                name="totalQuantity"
                label="Quantity"
                isNotDisplay={false}
                numberInputProps={{ min: 0, precision: 2 }}
                value={safeTotal.toFixed(2)}
              />
            </Col>
            <Col span={12}>
              <FormNumberInput
                name="quantityThreshold"
                label="Quantity Threshold"
                isNotDisplay={isNotDisplay}
                numberInputProps={{
                  defaultValue: 0,
                  min: 0,
                  precision: 2,
                }}
                value={quantityThreshold}
                displayStyle={safeTotal <= quantityThreshold && brokenStyle}
              />
            </Col>
          </Row>
          <Row gutter={20} justify="space-between" align="middle">
            <Col span={12}>
              <FormNumberInput
                name="totalQuantityAllocated"
                label="Quantity Allocated"
                isNotDisplay={false}
                numberInputProps={{ min: 0, precision: 2 }}
                value={totalQuantityAllocated.toFixed(2)}
              />
            </Col>
            <Col span={12}>
              <FormNumberInput
                name="quantityAvailable"
                label="Quantity Available"
                isNotDisplay={false}
                numberInputProps={{ min: 0, precision: 2 }}
                value={(safeTotal - totalQuantityAllocated).toFixed(2)}
              />
            </Col>
          </Row>
          <MaterialLocation
            id={id}
            isNotDisplay={isNotDisplay}
            locations={locations}
            onLocationsChanged={onLocationsChanged}
          />
        </>
      )}
      {!!isAssembly && (
        <AssemblyMaterials
          id={id}
          divisionId={divisionId}
          isNotDisplay={isNotDisplay}
          assemblyMaterials={assemblyMaterials}
          onChanged={onMaterialsChanged}
        />
      )}
    </div>
  );
}

MaterialInfo.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number,
  }),
  isNotDisplay: PropTypes.bool,
  divisionId: PropTypes.string,
  onDivisionChange: PropTypes.func,
  locations: PropTypes.arrayOf(PropTypes.shape({})),
  onLocationsChanged: PropTypes.func,
  totalQuantity: PropTypes.number,
  totalQuantityAllocated: PropTypes.number,
  isAssembly: PropTypes.bool,
  assemblyMaterials: PropTypes.arrayOf(PropTypes.shape({})),
  onMaterialsChanged: PropTypes.func,
  shouldAutoCalcCost: PropTypes.bool.isRequired,
  shouldAutoCalcLabourCost: PropTypes.bool.isRequired,
  projectId: PropTypes.string,
};

MaterialInfo.defaultProps = {
  item: null,
  isNotDisplay: false,
  divisionId: null,
  onDivisionChange: () => {},
  locations: [],
  onLocationsChanged: () => {},
  totalQuantity: 0,
  totalQuantityAllocated: 0,
  isAssembly: false,
  assemblyMaterials: [],
  onMaterialsChanged: () => {},
  projectId: undefined,
};
